import * as orderDisplayUtil from "../../lib/orderDisplayUtil";
import * as posModels from "../../lib/api/posModels";
import cn from "classnames";
import ErrorModal from "../ErrorModal";
import ManagerHeader from "../ManagerHeader";
import OrderDetailsDrawer from "../OrderDetailsDrawer";
import OrderFilterDrawer from "../OrderFilterDrawer";
import OutlineButton from "../OutlineButton";
import React, {useMemo, useState, useContext} from "react";
import SendReceiptDrawer from "../SendReceiptDrawer/SendReceiptDrawer";
import sortBy from "lodash.sortby";
import Spinner from "../Spinner";
import styles from "./ManagerPreviousOrders.module.css";
import TextModal from "../TextModal";
import useRefreshOrders from "../../lib/useRefreshOrders";
import {
    Filters,
    getDefaultFilters,
    getFilteredOrders,
} from "../../lib/filterOrders";
import {Lib} from "habit-core";
import {StationContext} from "../StationContext";
import {useAppSelector} from "../../lib/hooks";

const strings = {
    checkSearch: "Check Search",
    filter: "Filter Orders",
    orderNumber: "Order #",
    viewDetails: "Details / Receipt",
    noOrders: "No Orders",
    byDate: "By Date",
    bySource: "By Source",
    updateResults: "Update Results",
    searchBy: "Search by Name, Check or Amount",
    today: "Today",
    clearFilters: "Clear Filters",
    filtersApplied: (num: number) =>
        `${num} ${num === 1 ? "Filter" : "Filters"} Applied`,
    requestSent: "Request Sent",
    confirmPrinted:
        "Your request to print this order's receipt has been submitted",
    ok: "OK",
    pagerNumberLabel: "pgr. ",
    cancelledStub: " - CANCELLED",
    erroredStub: " - ERROR",
};

export default function ManagerPreviousOrders() {
    const {isTraining} = useContext(StationContext);
    const [errorMessage, setErrorMessage] = useState("");
    const [orderDetails, setOrderDetails] =
        useState<posModels.OrderParsed | null>(null);
    const [confirmPrinted, setConfirmPrinted] = useState(false);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<Filters>(
        getDefaultFilters(),
    );
    const [showSendReceipt, setShowSendReceipt] = useState(false);

    const ordersById = useAppSelector((state) => state.pos.orders.byId);

    const {initialLoading, initialErrorMessage, closeInitialErrorMessage} =
        useRefreshOrders(["paid", "cancelled", "error"], [], isTraining);

    const numActiveFilters =
        activeFilters.dates.length +
        (activeFilters.stationModes.length
            ? Math.ceil(activeFilters.stationModes.length / 2) - 1
            : 0) +
        activeFilters.sources.length +
        (activeFilters.text ? 1 : 0);

    const sortedOrders = useMemo(() => {
        return sortBy(
            Object.values(ordersById),
            (o) => o.orderDate.getDate(),
            (o) => o.orderNumber,
        ).reverse();
    }, [ordersById]);

    const filteredOrders = useMemo(() => {
        return getFilteredOrders(sortedOrders, activeFilters);
    }, [sortedOrders, activeFilters]);

    const header = (
        <ManagerHeader
            label={strings.checkSearch}
            extraText={
                numActiveFilters !== 0
                    ? strings.filtersApplied(numActiveFilters)
                    : undefined
            }
            buttonLabel={strings.filter}
            buttonDisabled={!sortedOrders.length}
            buttonOnClick={() => setIsFilterDrawerOpen(true)}
        />
    );

    if (initialLoading) {
        return (
            <>
                {header}
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            </>
        );
    }

    function getGuestOrPagerNumber(
        guestName?: string | null,
        pagerNumber?: number | null,
    ) {
        let content: string | undefined = undefined;
        if (guestName) {
            content = guestName;
        } else if (pagerNumber) {
            content = strings.pagerNumberLabel + pagerNumber;
        }
        return content;
    }

    function getStationNameOrSourceText(
        source: posModels.OrderSource,
        type: posModels.OrderType,
        stationMode?: posModels.StationMode | null,
        laneAssignment?: 1 | 2,
    ) {
        const stationNameText = orderDisplayUtil.getStationNameText(
            stationMode,
            laneAssignment,
        );
        if (source === "ez_cater" || source === "kiosk" || !stationNameText) {
            return orderDisplayUtil.getOrderSourceText(source, type);
        }
        return stationNameText;
    }

    return (
        <>
            {header}
            <div className={styles.ordersContainer}>
                {filteredOrders.length === 0 ? (
                    <div className={styles.noOrders}>{strings.noOrders}</div>
                ) : null}
                {filteredOrders.map((o) => {
                    const compCardPaidCents = o.transactions.reduce(
                        (sum, t) => {
                            return t.tender === "comp_card"
                                ? sum + t.amountCents
                                : sum;
                        },
                        0,
                    );

                    return (
                        <div key={o.id} className={styles.orderRow}>
                            <div className={cn(styles.rowSection, styles.info)}>
                                <div>
                                    <div className={styles.orderNumber}>
                                        <span>
                                            {strings.orderNumber}
                                            {o.orderNumber}
                                            {o.status === "cancelled"
                                                ? strings.cancelledStub
                                                : ""}
                                            {o.status === "error" ? (
                                                <span
                                                    className={
                                                        styles.erroredStub
                                                    }>
                                                    {strings.erroredStub}
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                    <div className={styles.stationName}>
                                        {getStationNameOrSourceText(
                                            o.source,
                                            o.type,
                                            o.stationMode,
                                            o.driveThruDetails?.laneNumber,
                                        )}
                                    </div>
                                    <div className={styles.orderTime}>
                                        {Lib.dates.format(
                                            o.orderDate,
                                            "MM/DD/YY",
                                        )}
                                        {" : "}
                                        {Lib.dates.format(
                                            o.orderDate,
                                            "hh:mm:ssa",
                                        )}
                                    </div>
                                </div>
                                <div className={styles.orderInfoRight}>
                                    <div className={styles.orderTotal}>
                                        {Lib.currency.centsToDollarString(
                                            Math.max(
                                                0,
                                                o.totalCents -
                                                    compCardPaidCents,
                                            ),
                                        )}
                                    </div>
                                    {getGuestOrPagerNumber(
                                        o.guestName,
                                        o.pagerNumber,
                                    ) ? (
                                        <div className={styles.orderName}>
                                            {getGuestOrPagerNumber(
                                                o.guestName,
                                                o.pagerNumber,
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className={styles.rowSection}>
                                <OutlineButton
                                    className={styles.orderButton}
                                    label={strings.viewDetails}
                                    onClick={() => setOrderDetails(o)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            {orderDetails !== null && !showSendReceipt ? (
                <OrderDetailsDrawer
                    order={orderDetails}
                    onClose={() => {
                        setOrderDetails(null);
                    }}
                    onSendReceipt={() => {
                        setShowSendReceipt(true);
                    }}
                    showReprintButton={true}
                    showSendReceipt={true}
                />
            ) : null}
            {orderDetails !== null && showSendReceipt ? (
                <SendReceiptDrawer
                    order={orderDetails}
                    onClose={() => {
                        setShowSendReceipt(false);
                        setOrderDetails(null);
                    }}
                />
            ) : null}
            {isFilterDrawerOpen ? (
                <OrderFilterDrawer
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                    onClose={() => setIsFilterDrawerOpen(false)}
                />
            ) : null}
            {errorMessage || initialErrorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage || initialErrorMessage}
                    onClose={() =>
                        errorMessage
                            ? setErrorMessage("")
                            : closeInitialErrorMessage()
                    }
                    showITInfo
                />
            ) : null}
            {confirmPrinted ? (
                <TextModal
                    onClose={() => setConfirmPrinted(false)}
                    title={strings.requestSent}
                    content={strings.confirmPrinted}
                    cancelLabel={strings.ok}
                />
            ) : null}
        </>
    );
}
